import { quoteErrorKey } from "./quote-error";
/** Order controls never render estimates; prices belong to an explicitly generated snapshot. */
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchCustomerPricing, fetchSupplierPricing } from "Redux/middlwares/supplierPricing";
import { createOrderQuote, setOrderPriceList } from "Services/supplier-pricing";
import { PBtn, PSelect } from "./ui";
import QuoteModal from "./quote-modal";
import { QuoteSnapshot, QuoteTotals } from "./quote-document";

type Props = { orderId?: number | null; supplierCustomerId?: number | null; business?: any; priceList?: string; onPriceListChange?: (key: string) => void; onQuoteGenerated?: (snapshot: QuoteSnapshot) => void; onPreview?: (snapshot: QuoteSnapshot) => void };

export default function OrderPricingPanel({ orderId, supplierCustomerId, priceList = "", onPriceListChange, onQuoteGenerated, onPreview }: Props) {
  const { t } = useTranslation("supplierPricing");
  const dispatch = useAppDispatch();
  const customerPricing = useAppSelector((state) => state.supplierPricing.customerPricing);
  const [key, setKey] = useState(priceList);
  const [busy, setBusy] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState(false);
  const [quote, setQuote] = useState<QuoteSnapshot | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const savedSelection = useRef(priceList);
  savedSelection.current = priceList;
  const request = useRef(0);
  const pending = useRef(false);
  const customer = supplierCustomerId ? customerPricing[supplierCustomerId] : null;
  const hasCustom = !customerLoading && !customerError && customer?.mode === "custom";

  useEffect(() => { dispatch(fetchSupplierPricing()); }, [dispatch]);
  useEffect(() => {
    let active = true;
    request.current += 1;
    pending.current = false;
    setKey(savedSelection.current); setQuote(null); setQuoteOpen(false); setBusy(false);
    setCustomerError(false); setCustomerLoading(Boolean(supplierCustomerId));
    if (supplierCustomerId) {
      Promise.resolve(dispatch(fetchCustomerPricing(supplierCustomerId)))
        .catch(() => { if (active) setCustomerError(true); })
        .finally(() => { if (active) setCustomerLoading(false); });
    }
    return () => { active = false; request.current += 1; };
  }, [orderId, supplierCustomerId, dispatch]);

  useEffect(() => { setKey(priceList); }, [priceList]);

  const choose = async (next: string) => {
    if (!next || pending.current) return;
    if (!orderId) {
      setKey(next); setQuote(null); setQuoteOpen(false);
      onPriceListChange?.(next);
      return;
    }
    pending.current = true;
    const token = ++request.current;
    setBusy(true); setQuote(null); setQuoteOpen(false);
    try {
      await setOrderPriceList(orderId, next);
      if (token === request.current) { setKey(next); onPriceListChange?.(next); }
    } catch {
      if (token === request.current) { setKey(""); toast.error(t("order.selectionFailed")); }
    } finally {
      if (token === request.current) { pending.current = false; setBusy(false); }
    }
  };

  const generate = async () => {
    if (!orderId || !key || pending.current) return;
    pending.current = true;
    const token = ++request.current;
    setBusy(true); setQuote(null); setQuoteOpen(false);
    try {
      const response = await createOrderQuote(orderId, key);
      const snapshot = response.data.snapshot as QuoteSnapshot;
      if (!snapshot || snapshot.price_list !== key || snapshot.order.id !== orderId) {
        throw new Error("Quote does not match the explicit selection");
      }
      if (token === request.current) {
        if (onQuoteGenerated) onQuoteGenerated(snapshot);
        else { setQuote(snapshot); if (onPreview) onPreview(snapshot); else setQuoteOpen(true); }
      }
    } catch (error) {
      if (token === request.current) toast.error(t(quoteErrorKey(error)));
    } finally {
      if (token === request.current) { pending.current = false; setBusy(false); }
    }
  };

  return <div className="eb-pr eb-pr-pricing-panel">
    <div className="eb-pr-side">
      <label className="eb-pr-label">{t("order.priceList")}</label>
      <PSelect value={key} onChange={choose} disabled={busy}
        ariaLabel={t("order.priceList")} options={[
          { value: "", label: t("order.chooseList") },
          { value: "default", label: t("tabs.default") },
          ...(hasCustom ? [{ value: `customer:${supplierCustomerId}`, label: `${t("order.customerList")} — ${customer.customerName || supplierCustomerId}` }] : []),
        ]} />
      {customerLoading && <p role="status">{t("common.loading")}</p>}
      {!customerLoading && <p>{t(customerError ? "order.customerLoadFailed" : hasCustom ? "order.explicitSelection" : "order.noCustomList")}</p>}
      {!orderId && <p>{t("order.saveToApplyServer")}</p>}
      <PBtn kind="quote" disabled={!orderId || !key || busy} onClick={generate}>
        {busy ? t("common.loading") : t("order.createQuote")}
      </PBtn>
    </div>
    {quote && <section className="eb-pr-summary" aria-label={t("order.summary")}>
      <h3>{t("quote.title", { no: quote.quote_no })}</h3>
      <p>{t(quote.price_list === "default" ? "tabs.default" : "order.customerList")}</p>
      <QuoteTotals snapshot={quote} />
      <PBtn kind="secondary" onClick={() => onPreview ? onPreview(quote) : setQuoteOpen(true)}>{t("quote.preview")}</PBtn>
    </section>}
    {quote && !onPreview && <QuoteModal show={quoteOpen} onHide={() => setQuoteOpen(false)} snapshot={quote} />}
  </div>;
}
